import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '../../shared/models/user.model';
import {
  PaginatedResult,
  PaginationFilter,
} from '../../shared/models/pagination.model';
import { CrudService } from '../../shared/models/crud-service.model';
import {
  UserStatusStatistics,
  UserSubscriptionStatistics,
} from '../../shared/models/statistics.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService implements CrudService<User> {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.backendUrl + 'users';

  loadWithFilter(filter: PaginationFilter): Observable<PaginatedResult<User>> {
    let httpParams = new HttpParams()
      .set('page', filter.page.toString())
      .set('limit', filter.limit.toString());

    if (filter.search) {
      httpParams = httpParams.set('search', filter.search);
    }

    return this.http.get<PaginatedResult<User>>(this.apiUrl, {
      params: httpParams,
    });
  }

  loadById(id: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  create(entity: Omit<User, 'id'>): Observable<User> {
    return this.http.post<User>(this.apiUrl, entity);
  }

  update(id: string, entity: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}`, entity);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  deleteMany(ids: string[]): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}`, {
      body: {
        ids,
      },
    });
  }

  updatePassword(
    id: string,
    passwordData: {
      currentPassword: string;
      newPassword: string;
      newPasswordConfirmation: string;
    }
  ): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      `${this.apiUrl}/${id}/password`,
      passwordData
    );
  }

  loadUserStatusStatistics(): Observable<UserStatusStatistics[]> {
    return this.http.get<UserStatusStatistics[]>(
      `${this.apiUrl}/dashboard/status`
    );
  }

  loadUserSubscriptionStatistics(): Observable<UserSubscriptionStatistics[]> {
    return this.http.get<UserSubscriptionStatistics[]>(
      `${this.apiUrl}/dashboard/subscriptions`
    );
  }

  getTotalCount(): Observable<{ total: number }> {
    return this.http.get<{ total: number }>(`${this.apiUrl}/count`);
  }
}
